// Avis d'imposition saisi par un agent (POST /recettes/creances, schéma schemaSaisieAvis) : redevable
// existant ou nouveau, rubrique du catalogue, base de calcul selon la rubrique, période, échéance,
// libellé ; le montant et la règle sont calculés par le serveur (simulation avant l'émission).
import { Bandeau, Bouton, Champ, DateChamp, Dialogue, GrilleChamps, Selection } from '@cc/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { requete } from '../../api/client';
import { useAction } from '../../commun/useAction';
import { LIBELLES_BASE, francs, tarifEnVigueur, texteTarif, type CreanceReponse, type RubriqueReponse } from './donnees';
import { INVALIDER_RECETTES, useRedevables, useRubriques } from './requetes';
import styles from './recettes.module.css';

interface Tarification {
  montant: number;
  regle: string;
  fictif: boolean;
}

const VIDE = { mode: 'existant', redevable_id: '', nom: '', type: 'personne', rubrique: '', surface: '', quantite: '', reference: '', debut: '', fin: '', echeance: '', libelle: '' };

function base(s: typeof VIDE, r: RubriqueReponse | undefined) {
  if (r?.base_calcul === 'surface') return { surface_m2: Number(s.surface.replace(',', '.')) };
  if (r?.base_calcul === 'unite') return { quantite: Number(s.quantite) };
  if (r?.base_calcul === 'pourcentage') return { montant_reference: Number(s.reference) };
  return {};
}

export function FormulaireAvis({ ouvert, onFermer }: { ouvert: boolean; onFermer: () => void }) {
  const navigate = useNavigate();
  const rubriques = useRubriques();
  const redevables = useRedevables();
  const [s, setS] = useState(VIDE);
  const [apercu, setApercu] = useState<Tarification | null>(null);
  const rubrique = (rubriques.data ?? []).find((r) => r.code === s.rubrique);
  const periode = { debut: s.debut, fin: s.fin || s.debut };
  const simulation = useAction<undefined, Tarification>({
    executer: () => requete<Tarification>('/recettes/tarification', { methode: 'POST', corps: { rubrique_code: s.rubrique, base: base(s, rubrique), periode } }),
    apres: (r) => setApercu(r),
  });
  const emission = useAction<undefined, CreanceReponse>({
    executer: () =>
      requete<CreanceReponse>('/recettes/creances', {
        methode: 'POST',
        corps: {
          redevable: s.mode === 'existant' ? { redevable_id: s.redevable_id, type: s.type } : { nom: s.nom.trim(), type: s.type },
          rubrique_code: s.rubrique,
          base: base(s, rubrique),
          periode,
          echeance: s.echeance,
          libelle: s.libelle.trim(),
        },
      }),
    succes: (c) => `Créance ${c.numero} émise pour ${francs(c.montant_du)}.`,
    invalider: INVALIDER_RECETTES,
    apres: (c) => {
      onFermer();
      setS(VIDE);
      setApercu(null);
      void navigate(`/recettes/creances/${c.id}`);
    },
  });
  const champ = (cle: keyof typeof VIDE) => (e: { target: { value: string } }) => {
    setApercu(null);
    setS((x) => ({ ...x, [cle]: e.target.value }));
  };
  const erreur = (champNom: string) => emission.erreursChamps.find((x) => x.champ === champNom || x.champ.startsWith(`${champNom}.`))?.message;
  const dateOk = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);
  const redevableOk = s.mode === 'existant' ? Boolean(s.redevable_id) : s.nom.trim().length > 0;
  const baseOk = !rubrique || rubrique.base_calcul === 'forfait' || (rubrique.base_calcul === 'surface' ? Number(s.surface.replace(',', '.')) > 0 : rubrique.base_calcul === 'unite' ? Number(s.quantite) > 0 : Number(s.reference) >= 0 && s.reference !== '');
  const complet = redevableOk && Boolean(rubrique) && baseOk && dateOk(s.debut) && dateOk(s.echeance) && s.libelle.trim().length > 0;
  const vigueur = rubrique ? tarifEnVigueur(rubrique, s.debut || undefined) : null;
  return (
    <Dialogue
      ouvert={ouvert}
      titre="Saisir un avis d'imposition"
      onFermer={onFermer}
      actions={
        <>
          <Bouton variante="secondaire" onClick={onFermer}>
            Annuler
          </Bouton>
          <Bouton variante="secondaire" chargement={simulation.enCours} disabled={!complet} onClick={() => simulation.lancer(undefined)}>
            Calculer le montant
          </Bouton>
          <Bouton chargement={emission.enCours} disabled={!complet} onClick={() => emission.lancer(undefined)}>
            Émettre la créance
          </Bouton>
        </>
      }
    >
      <p>La créance est numérotée et tarifée par le serveur avec le tarif approuvé en vigueur au début de la période ; le redevable la voit dans son espace s&apos;il a un compte.</p>
      <GrilleChamps>
        <Selection id="avis-mode" libelle="Redevable" value={s.mode} options={[{ valeur: 'existant', libelle: 'Redevable connu' }, { valeur: 'nouveau', libelle: 'Nouveau redevable' }]} onChange={champ('mode')} />
        {s.mode === 'existant' ? (
          <Selection id="avis-redevable" libelle="Nom du redevable" required optionVide="Choisir" options={(redevables.data ?? []).map((r) => ({ valeur: r.id, libelle: r.nom }))} value={s.redevable_id} erreur={erreur('redevable')} onChange={champ('redevable_id')} />
        ) : (
          <Champ id="avis-nom" libelle="Nom du redevable" required value={s.nom} erreur={erreur('redevable')} onChange={champ('nom')} />
        )}
        <Selection id="avis-type" libelle="Type" value={s.type} options={[{ valeur: 'personne', libelle: 'Personne' }, { valeur: 'entreprise', libelle: 'Entreprise' }]} onChange={champ('type')} />
        <Selection id="avis-rubrique" libelle="Rubrique" required optionVide="Choisir" options={(rubriques.data ?? []).filter((r) => r.categorie === 'principale' && r.active).map((r) => ({ valeur: r.code, libelle: r.libelle }))} value={s.rubrique} erreur={erreur('rubrique_code')} onChange={champ('rubrique')} />
        {rubrique?.base_calcul === 'surface' ? <Champ id="avis-surface" libelle="Surface (m²)" inputMode="decimal" required value={s.surface} erreur={erreur('base')} onChange={champ('surface')} /> : null}
        {rubrique?.base_calcul === 'unite' ? <Champ id="avis-quantite" libelle="Quantité" inputMode="numeric" required value={s.quantite} erreur={erreur('base')} onChange={champ('quantite')} /> : null}
        {rubrique?.base_calcul === 'pourcentage' ? <Champ id="avis-reference" libelle="Montant de référence (F)" inputMode="numeric" required value={s.reference} erreur={erreur('base')} onChange={champ('reference')} /> : null}
        <DateChamp id="avis-debut" libelle="Début de la période" required value={s.debut} erreur={erreur('periode')} onChange={champ('debut')} />
        <DateChamp id="avis-fin" libelle="Fin de la période" facultatif aide="Même jour que le début si vide" value={s.fin} onChange={champ('fin')} />
        <DateChamp id="avis-echeance" libelle="Échéance de paiement" required value={s.echeance} erreur={erreur('echeance')} onChange={champ('echeance')} />
      </GrilleChamps>
      <Champ id="avis-libelle" libelle="Libellé de la créance" required value={s.libelle} erreur={erreur('libelle')} onChange={champ('libelle')} />
      {rubrique ? (
        <p className={styles.note}>
          Base {LIBELLES_BASE[rubrique.base_calcul] ?? rubrique.base_calcul} ; {vigueur ? `tarif en vigueur : ${texteTarif(vigueur)}` : 'aucun tarif approuvé en vigueur à cette date (le serveur refusera l\'émission)'}.
        </p>
      ) : null}
      {apercu ? (
        <div role="status">
          <p>
            Montant calculé : <strong>{francs(apercu.montant)}</strong>
            {apercu.fictif ? ' (tarif fictif de présentation)' : ''}
          </p>
          <p className={styles.regle}>{apercu.regle}</p>
        </div>
      ) : null}
      {simulation.messageErreur ? <Bandeau type="erreur">{simulation.messageErreur}</Bandeau> : null}
      {emission.messageErreur ? <Bandeau type="erreur">{emission.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}
